// PERFECT 10 — read hook.
//
// Reads the current active Perfect 10 slip (cross-tier conviction distillation)
// via get_perfect10_current RPC, plus the generation history via
// get_perfect10_history. Mirrors usePowerballWeek's shape/conventions.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  calendarDateInAccra,
} from "../predictions/sessionDate.ts";

// ─── Types ────────────────────────────────────────────────

export type Perfect10Tier = "pb10" | "mega25" | "mixed_mega" | "lower_lines" | "goals_bank" | "big_stage" | "multiverse";

export interface Perfect10Leg {
  id: string;
  rank: number;
  source_tier: Perfect10Tier;
  fixture_id: string | null;
  match_name: string;
  league: string;
  league_short: string | null;
  country_code: string | null;
  kickoff_at: string | null;
  market: string;
  pick: string;
  odds: number;
  conviction_score: number;
  score_base_confidence: number | null;
  score_form_rating: number | null;
  score_h2h_weight: number | null;
  score_market_depth: number | null;
  score_recency_boost: number | null;
  score_odds_value: number | null;
}

export interface Perfect10Slip {
  id: string;
  publication_id?: string | null;
  publication_version?: number | null;
  week_number: number;
  generated_at: string | null;
  triggered_by: string | null;
  avg_conviction: number | null;
  status: "active" | "archived" | "failed";
  source_tiers: Record<string, number>;
  legs: Perfect10Leg[];
}

export interface Perfect10HistoryRow {
  id: string;
  week_number: number;
  generated_at: string | null;
  triggered_by: string | null;
  avg_conviction: number | null;
  status: string;
  failure_reason: string | null;
  leg_count: number;
}

export interface UsePerfect10State {
  weekNumber: number;
  slip: Perfect10Slip | null;
  history: Perfect10HistoryRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// ─── Parsers ──────────────────────────────────────────────

function parseLeg(raw: Record<string, unknown>): Perfect10Leg {
  const num = (v: unknown): number | null => (typeof v === "number" ? v : null);
  return {
    id: String(raw.id ?? ""),
    rank: Number(raw.rank ?? 0),
    source_tier: (raw.source_tier as Perfect10Tier) ?? "pb10",
    fixture_id: typeof raw.fixture_id === "string" ? raw.fixture_id : null,
    match_name: String(raw.match_name ?? ""),
    league: String(raw.league ?? ""),
    league_short: typeof raw.league_short === "string" ? raw.league_short : null,
    country_code: typeof raw.country_code === "string" ? raw.country_code : null,
    kickoff_at: typeof raw.kickoff_at === "string" ? raw.kickoff_at : null,
    market: String(raw.market ?? ""),
    pick: String(raw.pick ?? ""),
    odds: Number(raw.odds ?? 0),
    conviction_score: Number(raw.conviction_score ?? 0),
    score_base_confidence: num(raw.score_base_confidence),
    score_form_rating: num(raw.score_form_rating),
    score_h2h_weight: num(raw.score_h2h_weight),
    score_market_depth: num(raw.score_market_depth),
    score_recency_boost: num(raw.score_recency_boost),
    score_odds_value: num(raw.score_odds_value),
  };
}

function parseSlip(raw: Record<string, unknown>): Perfect10Slip {
  const legsRaw = Array.isArray(raw.legs) ? (raw.legs as Record<string, unknown>[]) : [];
  const legs = legsRaw.map(parseLeg);
  const generatedAt = typeof raw.generated_at === "string" ? raw.generated_at : null;

  return {
    id: String(raw.id ?? ""),
    week_number: Number(raw.week_number ?? 0),
    publication_id: typeof raw.publication_id === "string" ? raw.publication_id : null,
    publication_version: typeof raw.publication_version === "number" ? raw.publication_version : null,
    generated_at: generatedAt,
    triggered_by: typeof raw.triggered_by === "string" ? raw.triggered_by : null,
    avg_conviction: typeof raw.avg_conviction === "number" ? raw.avg_conviction : null,
    status: (raw.status as Perfect10Slip["status"]) ?? "active",
    source_tiers: (raw.source_tiers as Record<string, number>) ?? {},
    legs,
  };
}

function parseHistoryRow(raw: Record<string, unknown>): Perfect10HistoryRow {
  return {
    id: String(raw.id ?? ""),
    week_number: Number(raw.week_number ?? 0),
    generated_at: typeof raw.generated_at === "string" ? raw.generated_at : null,
    triggered_by: typeof raw.triggered_by === "string" ? raw.triggered_by : null,
    avg_conviction: typeof raw.avg_conviction === "number" ? raw.avg_conviction : null,
    status: String(raw.status ?? ""),
    failure_reason: typeof raw.failure_reason === "string" ? raw.failure_reason : null,
    leg_count: Number(raw.leg_count ?? 0),
  };
}

// ─── Hook ─────────────────────────────────────────────────

export function usePerfect10(): UsePerfect10State {
  const { user } = useAuthSession();
  const [weekNumber, setWeekNumber] = useState(0);
  const [slip, setSlip] = useState<Perfect10Slip | null>(null);
  const [history, setHistory] = useState<Perfect10HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) return;
    const refresh = () => {
      if (document.visibilityState === "visible") refetch();
    };
    const interval = window.setInterval(refresh, 2 * 60 * 1_000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [user, refetch]);

  useEffect(() => {
    if (!user) {
      setSlip(null);
      setHistory([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);
    setError(null);

    // Call via .call(client, …) so `this` stays bound to the Supabase client —
    // extracting `client.rpc` into a bare variable loses `this`, and the
    // client's internal `this.rest` access then throws
    // "Cannot read properties of undefined (reading 'rest')".
    const rpc = (
      fn: string,
      args?: Record<string, unknown>,
    ): Promise<{ data: Record<string, unknown> | null; error: { message: string } | null }> =>
      (client.rpc as unknown as (
        f: string,
        a?: Record<string, unknown>,
      ) => Promise<{ data: Record<string, unknown> | null; error: { message: string } | null }>)
        .call(client, fn, args);

    void (async () => {
      try {
        const [currentRes, historyRes] = await Promise.all([
          rpc("get_perfect10_current"),
          rpc("get_perfect10_history", { p_limit: 20 }),
        ]);

        if (cancelled) return;

        if (currentRes.error) {
          setError(currentRes.error.message);
          setLoading(false);
          return;
        }

        const cur = (currentRes.data ?? {}) as Record<string, unknown>;
        setWeekNumber(Number(cur.week_number ?? 0));
        if (cur.slip && typeof cur.slip === "object") {
          const parsed = parseSlip(cur.slip as Record<string, unknown>);
          const today = calendarDateInAccra(new Date());
          const generatedDate = parsed.generated_at
            ? calendarDateInAccra(parsed.generated_at)
            : null;
          setSlip(today !== null && generatedDate === today ? parsed : null);
        } else {
          setSlip(null);
        }

        if (!historyRes.error && historyRes.data) {
          const h = historyRes.data as Record<string, unknown>;
          const rows = Array.isArray(h.slips) ? (h.slips as Record<string, unknown>[]) : [];
          setHistory(rows.map(parseHistoryRow));
        }

        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user, tick]);

  return useMemo(
    () => ({ weekNumber, slip, history, loading, error, refetch }),
    [weekNumber, slip, history, loading, error, refetch],
  );
}
